import { useCallback } from "react"

import toast from "react-hot-toast"
import { Link } from "react-router-dom"
import { Button, Card, Header, Icon, Message } from "semantic-ui-react"

import { UserChip } from "components/lib/Chips"
import { useAppSelector } from "store"
import { useUserReload } from "utils/useUserReload"
import util from "utils/utils"

import InviteeChip from "./InviteeChip"
import type { InviteCardProps } from "./types"

const GroupInviteCard = ({
  invitation,
  acceptInvite,
  rejectInvite,
  removeInvite,
  cardView = true,
}: InviteCardProps) => {
  const reloadUser = useUserReload()
  const user = useAppSelector((state) => state.user)
  const { inviterUser, group, createdByCurrentUser } = invitation

  const accept = useCallback(() => {
    util
      .confirm("Join group", `Are you sure you want to join "${group.name}"?`)
      .then(() => {
        acceptInvite(
          invitation,
          () => {
            reloadUser()
            toast.success("Joined group")
          },
          () => toast.error("Error joining group"),
        )
      })
      .catch(() => {})
  }, [acceptInvite, invitation, group?.name, reloadUser])

  const reject = useCallback(() => {
    util
      .confirm(
        "Reject group invitation",
        `Are you sure you want to reject the invitation to join "${group?.name || "Deleted group"}"?`,
      )
      .then(() => {
        rejectInvite(
          invitation,
          () => {
            toast("Invitation rejected")
          },
          () => toast.error("Error rejecting invite"),
        )
      })
      .catch(() => {})
  }, [rejectInvite, invitation, group?.name])

  const remove = useCallback(() => {
    util
      .confirm(
        "Remove group invitation",
        `Are you sure you want to remove this invitation to join "${group?.name || "Deleted group"}"?`,
      )
      .then(() => {
        removeInvite(
          invitation,
          () => {
            toast("Invitation removed")
          },
          () => toast.error("Error removing invite"),
        )
      })
      .catch(() => {})
  }, [removeInvite, invitation, group?.name])

  if (!group) {
    invitation.canAccept = false
  }

  return (
    <div style={cardView ? { display: "flex", justifyContent: "space-between", alignItems: "center" } : {}}>
      <div>
        {cardView ? (
          <Header
            style={{ marginBottom: "0.3em" }}
            as={Link}
            disabled={!group}
            to={group ? `/groups/${group._id}` : null}
          >
            {group?.name || "Deleted group"}
          </Header>
        ) : (
          <Header as="h2" icon textAlign="center">
            <Icon name="users" circular />
            <Header.Content>Join {group?.name || `Deleted group`}</Header.Content>
          </Header>
        )}

        <Card.Description>
          {createdByCurrentUser ? (
            <InviteeChip invitation={invitation} preText="You invited" postText="to join this group" />
          ) : (
            <UserChip compact="very" user={inviterUser} postText="has invited you to join this group" />
          )}
        </Card.Description>
      </div>
      <Card.Content extra>
        <div className="ui" style={cardView ? {} : { marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
          {invitation.acceptedAt && group && (
            <Button basic color="green" as={Link} to={`/groups/${group?._id}`}>
              Go to group
            </Button>
          )}
          {!invitation.acceptedAt && (
            <>
              {!group && (
                <Message error style={{ width: "100%" }}>
                  This invitation to join is no longer valid because the group has been deleted.
                </Message>
              )}
              {user && invitation.canRemove ? (
                <Button
                  basic
                  color={createdByCurrentUser ? null : "red"}
                  onClick={createdByCurrentUser ? remove : reject}
                >
                  {createdByCurrentUser ? "Remove" : "Reject"}
                </Button>
              ) : null}
              {user && invitation.canAccept ? (
                <Button basic color="green" onClick={accept}>
                  Accept
                </Button>
              ) : null}
            </>
          )}
        </div>
      </Card.Content>
    </div>
  )
}

export default GroupInviteCard
